import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { cartAtom } from '@/atoms/Cart';
import { closeModalControl } from '@/atoms/closeModalControl';
import { countryAtom } from '@/atoms/countryState';

import { formateErrorObject } from '../shared/formateErrorObject';
import { notify } from '../shared/notify';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import {
  createRazorpayPayment,
  handlePaymentFailed,
} from '../../services/Cart';
import { getSyncBookListApi } from '../../services/Library/Books';
import { User } from '../../utils/interfaces/User';
import { Logo } from '../../../assets';

interface RazorpayData {
  amount?: {
    subTotal: number;
    discount: string;
    total: number;
    tax: number;
    currency: string;
    currencyCode: string;
    couponCode: { coupon_code: string }[];
  };
  razorpayOrderId: string;
  productIds?: string;
  orderId: string;
  userData: User;
  setFetchOrders?: (value: boolean) => void;
  fetchOrders?: boolean;
  setVisible?: (value: boolean) => void;
  paymentType: string;
  totalAmount?: number;
  isCartPage?: boolean;
  userPhone?: string;
  cartId: number;
  account?: 'razorpay' | 'razorpay_2';
}

type RazorpayInitShortParams = {
  amount: number;
  razorpayOrderId: string;
  orderId: string;
  name?: string;
  image?: string;
  userData: {
    name: string;
    email: string;
  };
  currency?: string;
  onSuccess: (response: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) => void;
  onFailure?: () => void;
};

export const usePayment = () => {
  const cartData = useRecoilValue(cartAtom);
  const router = useRouter();
  const [, setVisibleCloseModal] = useRecoilState(closeModalControl);
  const globalCountryState = useRecoilValue(countryAtom);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      const isCartStep1 =
        router.pathname === '/cart' && router.query.cartStep === '1';

      if (isCartStep1 && !globalCountryState?.globalCountryChange) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    [
      router.pathname,
      router.query.cartStep,
      globalCountryState?.globalCountryChange,
    ],
  );

  const manageBeforeUnloadListener = useCallback(() => {
    const isCartStep1 =
      router.pathname === '/cart' && router.query.cartStep === '1';

    const isIndia = globalCountryState?.selectedCountry?.code === 'IN';

    if (isCartStep1 && !globalCountryState?.globalCountryChange && isIndia) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [
    router.pathname,
    router.query.cartStep,
    handleBeforeUnload,
    globalCountryState?.globalCountryChange,
  ]);

  useEffect(() => {
    manageBeforeUnloadListener();

    router.events.on('routeChangeComplete', manageBeforeUnloadListener);

    if (globalCountryState?.globalCountryChange) {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      router.events.off('routeChangeComplete', manageBeforeUnloadListener);
    };
  }, [
    manageBeforeUnloadListener,
    handleBeforeUnload,
    router.events,
    globalCountryState?.globalCountryChange,
  ]);

  const buyNitroEvent = async (orderId: string) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.buy === 'function'
      ) {
        const currentUrl = window.location.href;
        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        // const utmData: Record<string, string> = {};
        // const searchParams = new URLSearchParams(window?.location?.search);
        // searchParams.forEach((value, key) => {
        //   if (key.startsWith('utm_')) {
        //     utmData[key] = value;
        //   }
        // });
        const buyData = {
          order_id: orderId, //
          checkout_url: currentUrl, //
          country: zebra_selected_country?.name, //
          currency: zebra_selected_country?.currency, //
          items: cartData?.map((i: any) => ({
            product_id: i?.product_id,
            title: i?.display_name,
            price: i?.price,
            currency: zebra_selected_country?.currency,
            product_url: `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/books/temp-${i?.product_id}`,
          })),
          // utm_data: utmData,
        };

        nitro.buy(buyData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initializeRazorpay = async ({
    amount,
    razorpayOrderId,
    orderId,
    userData,
    setFetchOrders,
    fetchOrders,
    setVisible,
    productIds,
    totalAmount,
    isCartPage = false,
    userPhone,
    cartId,
    account = 'razorpay',
  }: RazorpayData): Promise<void> => {
    const handleCreateRazorpayPayment = async (response: any) => {
      try {
        await createRazorpayPayment(
          {
            order_id: orderId,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          },
          cartId ?? Number(cartId),
        );

        buyNitroEvent(orderId);
        getSyncBookListApi();

        if (typeof window !== 'undefined') {
          router.replace({
            pathname: '/order-successful',
            query: {
              orderId: orderId,
              product_ids: productIds,
              callPurchased: 'purchased',
            },
          });
          window.history.replaceState(null, '', '/order-successful');
        }
        if (setFetchOrders) {
          setFetchOrders(!fetchOrders);
        }
      } catch (error: any) {
        const message = formateErrorObject(error);
        notify(message, 'error');
      }
    };

    const paymentFailed = async () => {
      try {
        await handlePaymentFailed(
          {
            order_id: orderId,
            razorpay_order_id: razorpayOrderId,
          },
          cartId ?? Number(cartId),
        );
        const eventParams: EventDetails = {
          event_name: 'order_failed',
          source: 'global',
          source_type: 'orders',
          source_id: orderId,
          sub_source: null,
          sub_source_id: null,
          unit: amount?.currencyCode || 'INR',
          currency: amount?.currencyCode || 'INR',
          value: totalAmount,
          meta: {
            payment_mode: 'prepaid',
          },
        };

        logEvent({
          eventDetails: eventParams,
          userData: userData,
          pathname: router.pathname as string,
        });
      } catch (error: any) {
        const message = formateErrorObject(error);
        notify(message, 'error');
      }
    };

    if (typeof window !== 'undefined') {
      const options = {
        key:
          account === 'razorpay_2'
            ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2!
            : process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
        amount: amount?.total || totalAmount!,
        currency: amount?.currencyCode || 'INR',
        name: 'ZebraLearn',
        image: Logo,
        order_id: razorpayOrderId,
        handler: (response: any) => {
          handleCreateRazorpayPayment(response);
        },
        modal: {
          escape: false,
          ondismiss: async () => {
            try {
              if (isCartPage) {
                setVisibleCloseModal(true);
                if (setVisible) {
                  setVisible(true);
                }
              }

              await paymentFailed();
            } catch (error) {
              console.error('Error in ondismiss:', error);
            }
          },
        },
        prefill: {
          name: userData.name,
          email: userData.email,
          currency: amount?.currencyCode || 'INR',
          contact: userPhone || userData.phone,
        },
      };

      if ((window as any).Razorpay) {
        const rzp = new (window as any).Razorpay(options);

        rzp.open();

        rzp.on('close', () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
          router.events.off('routeChangeComplete', manageBeforeUnloadListener);
        });
      } else {
        console.error('Razorpay SDK not loaded');
      }
    } else {
      console.error('Window object is undefined. Cannot initialize Razorpay.');
    }
  };

  const initializeShortRazorpay = async ({
    amount,
    razorpayOrderId,
    name = 'ZebraLearn',
    image,
    userData,
    onSuccess,
    onFailure,
    currency = 'INR',
  }: RazorpayInitShortParams): Promise<void> => {
    if (typeof window === 'undefined') {
      return;
    }

    const options = {
      key: process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
      amount,
      currency: currency,
      name,
      image,
      order_id: razorpayOrderId,
      handler: (response: any) => {
        onSuccess(response);
      },
      modal: {
        escape: false,
        ondismiss: () => {
          if (onFailure) onFailure();
        },
      },
      prefill: { name: userData.name, email: userData.email },
    };

    if ((window as any).Razorpay) {
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } else {
      console.error('Razorpay SDK not loaded');
    }
  };

  return {
    initializeRazorpay,
    initializeShortRazorpay,
  };
};
